import { AppRoute } from "../../routes/types";
import { Link, useLocation } from "react-router-dom";
import { Fragment, ReactNode, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { getIcon } from "../../util/iconMap";
import { useAppState } from "../../store/features/app/state";
import { useAuthenticationState } from "../../store/features/authentication/state";
import { useGetCustomToolsQuery } from "../../store/api/customToolApi";
import { useGetIpRequestPendingCountQuery } from "../../store/api/ipRequestApi";
import { NavButton } from "./NavButton";

// These items always appear at the bottom of the nav
const BOTTOM_IDS = new Set(["tech-docs", "admin", "settings"]);

interface SidebarProps {
	showLabels?: boolean;
	onNavClick?: () => void;
}

export const Sidebar = ({ showLabels = true, onNavClick }: SidebarProps) => {
	const { pathname } = useLocation();
	const { state: appState } = useAppState();
	const { state: authState } = useAuthenticationState();
	const menuItems = appState.menuItems();
	const profile = authState.profile();
	const { data: toolsData } = useGetCustomToolsQuery();
	const [expandedDepts, setExpandedDepts] = useState<Set<string>>(new Set());
	const [collapsedDepts, setCollapsedDepts] = useState<Set<string>>(new Set());

	const hasAccess = (item: AppRoute) => {
		if (item.requiredRoles && item.requiredRoles.length > 0) {
			if (!profile?.roles) return false;
			return item.requiredRoles.some((role) => profile.roles.includes(role));
		}
		if (item.toolSlug) {
			if (profile?.roles?.includes("admin")) return true;
			if (!profile?.toolAccess) return false;
			return profile.toolAccess.includes(item.toolSlug);
		}
		return true;
	};

	const visibleItems = menuItems.filter(hasAccess);

	// Red badge on the IP Requests nav item when requests are awaiting review
	const canSeeIpRequests =
		profile?.roles?.includes("admin") ||
		profile?.toolAccess?.includes("ip-requests");
	const { data: ipPending } = useGetIpRequestPendingCountQuery(undefined, {
		skip: !canSeeIpRequests,
		pollingInterval: 60000
	});
	const badgeFor = (slug?: string) =>
		slug === "ip-requests" ? (ipPending?.pendingCount ?? 0) : 0;

	// All tools from API (built-in + custom) with department info
	const allTools = (toolsData?.tools || []).filter((t) => {
		if (profile?.roles?.includes("admin")) return true;
		return profile?.toolAccess?.includes(t.slug);
	});

	// Built-in tool slugs that have a department assigned
	const builtInWithDept = new Set(
		allTools.filter((t) => !t.isCustom && t.department).map((t) => t.slug)
	);

	// Split main nav: top items (no dept), bottom items (docs, admin, settings)
	// Items with dept assigned are excluded from main nav
	const topItems = visibleItems.filter((item) => {
		if (BOTTOM_IDS.has(item.id)) return false;
		if (item.toolSlug && builtInWithDept.has(item.toolSlug)) return false;
		return true;
	});
	const bottomItems = visibleItems.filter((item) => BOTTOM_IDS.has(item.id));

	// Group tools with departments
	const toolsWithDept = allTools.filter((t) => t.department);
	const toolsByDept: Record<string, typeof toolsWithDept> = {};
	for (const tool of toolsWithDept) {
		const dept = tool.department!;
		if (!toolsByDept[dept]) toolsByDept[dept] = [];
		toolsByDept[dept].push(tool);
	}

	// Map built-in tool slugs to their route info
	const routeBySlug: Record<string, AppRoute> = {};
	for (const item of visibleItems) {
		if (item.toolSlug) routeBySlug[item.toolSlug] = item;
	}

	// Parse user's departments for auto-expand
	const userDepts: string[] = (() => {
		try {
			const parsed = JSON.parse(profile?.department || "[]");
			return Array.isArray(parsed) ? parsed : parsed ? [parsed] : [];
		} catch {
			return profile?.department ? [profile.department] : [];
		}
	})();

	// Auto-expand if current page is in dept OR user belongs to dept
	const shouldAutoExpand = (dept: string) => {
		if (userDepts.includes(dept)) return true;
		const tools = toolsByDept[dept] || [];
		return tools.some((tool) => {
			const route = routeBySlug[tool.slug];
			const url = tool.isCustom ? `/tools/${tool.slug}` : (route?.url || `/tools/${tool.slug}`);
			return pathname === url;
		});
	};

	// Sort tools within each department alphabetically
	for (const dept of Object.keys(toolsByDept)) {
		toolsByDept[dept].sort((a, b) => a.name.localeCompare(b.name));
	}

	const toggleDept = (dept: string) => {
		const isCurrentlyOpen = expandedDepts.has(dept) || (shouldAutoExpand(dept) && !collapsedDepts.has(dept));
		if (isCurrentlyOpen) {
			// Collapse: remove from expanded, add to collapsed
			setExpandedDepts((prev) => { const next = new Set(prev); next.delete(dept); return next; });
			setCollapsedDepts((prev) => { const next = new Set(prev); next.add(dept); return next; });
		} else {
			// Expand: add to expanded, remove from collapsed
			setExpandedDepts((prev) => { const next = new Set(prev); next.add(dept); return next; });
			setCollapsedDepts((prev) => { const next = new Set(prev); next.delete(dept); return next; });
		}
	};

	const renderNavLink = (item: AppRoute, index?: number) => (
		<Fragment key={item.id}>
			<Link
				to={item.url}
				aria-label={item.name}
				aria-current={pathname === item.url ? "page" : undefined}
				onClick={onNavClick}
				className="focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
			>
				<NavButton
					icon={item.icon as ReactNode}
					label={item.name}
					isActive={pathname === item.url}
					showLabel={showLabels}
					badge={badgeFor(item.toolSlug)}
				/>
			</Link>
			{index === 0 && (
				<div className="mx-4 my-2 border-t border-gray-200/60 dark:border-gray-700/60" />
			)}
		</Fragment>
	);

	return (
		<nav
			className="h-full w-[260px] glass-sidebar border-r border-gray-200/50 dark:border-gray-700/50 overflow-y-auto"
			aria-label="Main navigation"
		>
			<div className="flex flex-col py-2">
				{/* Top nav items (Ask Scout, ungrouped tools) */}
				{topItems.map((item, index) => renderNavLink(item, index))}

				{/* Department-grouped tools */}
				{Object.keys(toolsByDept).length > 0 && topItems.length > 1 && (
					<div className="mx-4 my-2 border-t border-gray-200/60 dark:border-gray-700/60" />
				)}
				{Object.entries(toolsByDept).sort(([a], [b]) => a.localeCompare(b)).map(([dept, tools]) => {
					const isOpen = expandedDepts.has(dept) || (shouldAutoExpand(dept) && !collapsedDepts.has(dept));

					return (
						<Fragment key={dept}>
							{showLabels ? (
								<button
									onClick={() => toggleDept(dept)}
									className="w-full flex items-center justify-between px-6 py-2 text-[11px] uppercase font-semibold text-gray-400 dark:text-gray-500 tracking-wider hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
								>
									<span>{dept}</span>
									<div className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}>
										<ChevronDownIcon className="h-3 w-3" />
									</div>
								</button>
							) : (
								<div className="mx-4 my-2 border-t border-gray-200/60 dark:border-gray-700/60" />
							)}
							<div className={`overflow-hidden transition-all duration-200 ${isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}>
								{tools.map((tool) => {
									const route = routeBySlug[tool.slug];
									const url = tool.isCustom ? `/tools/${tool.slug}` : (route?.url || `/tools/${tool.slug}`);
									const icon = route?.icon || getIcon(tool.icon || "WrenchScrewdriverIcon");

									return (
										<Link
											key={tool.id}
											to={url}
											aria-label={tool.name}
											aria-current={pathname === url ? "page" : undefined}
											onClick={onNavClick}
											className="focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
										>
											<NavButton
												icon={icon as ReactNode}
												label={tool.name}
												isActive={pathname === url}
												showLabel={showLabels}
												badge={badgeFor(tool.slug)}
											/>
										</Link>
									);
								})}
							</div>
						</Fragment>
					);
				})}

				{/* Bottom nav items (Documentation, User Management, Settings) */}
				{bottomItems.length > 0 && (
					<div className="mx-4 my-2 border-t border-gray-200/60 dark:border-gray-700/60" />
				)}
				{bottomItems.map((item) => renderNavLink(item))}
			</div>
		</nav>
	);
};
